import {Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {ObjectStorageService} from "../../core/object-storage.service";
import {OperationContainer} from "../../core/definitions/operation-container";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MetaField} from "../../core/definitions/meta-field";
import {FieldInputType} from "../../core/definitions/field-input-type.enum";
import {SuperObjectModel} from "../../core/definitions/super-object-model";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {ProgressDialogComponent} from "../../shared/progress-dialog/progress-dialog.component";
import {TranslatePipe} from "@ngx-translate/core";

export interface ProgressDialogData {
  operationContainer: OperationContainer;
}

@Component({
  selector: 'app-object-copy-progress-dialog',
  imports: [
    MatProgressSpinner,
    TranslatePipe
  ],
  templateUrl: './object-copy-progress-dialog.html',
  styleUrl: './object-copy-progress-dialog.scss'
})
export class ObjectCopyProgressDialog implements OnInit {
  private readonly dialogRef: MatDialogRef<ProgressDialogComponent> = inject(MatDialogRef);
  private readonly objectStorageService = inject(ObjectStorageService);
  private readonly operationData: ProgressDialogData = inject(MAT_DIALOG_DATA);

  fieldsToKeep: WritableSignal<MetaField[]> = signal([]);
  percentage: WritableSignal<number> = signal(0);
  progressText: WritableSignal<string> = signal('TRANS__COPY_PROGRESS_DIALOG__DESCRIPTION');
  preparing: WritableSignal<boolean> = signal(true);
  storeProgress: WritableSignal<number> = signal(0);
  storeTotal: WritableSignal<number> = signal(0);

  ngOnInit() {
    this.dialogRef.disableClose = true;

    if (this.operationData.operationContainer) {
      this.setFieldsToKeep();
      this.setTargetModelData()
        .then()
        .catch(error => {
          this.dialogRef.close(false);
        });
    }
  }

  private setFieldsToKeep() {
    let keepFields: MetaField[] = [];

    for (const section of this.operationData.operationContainer.currentOperation.$$sectionsContainers[0].sections) {
      for (const field of section.editFields) {
        // Field is selected
        if (field.keep && field.input_type !== FieldInputType.CONTEXT) {
          keepFields.push(field);
        }
      }
    }

    this.fieldsToKeep.set(keepFields);
  }

  private getValue(obj, keyPath) {
    const keys = keyPath.split('->');
    let value = obj;

    for (const key of keys) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        value = value[key];
      } else {
        return undefined;
      }
    }

    return value;
  }


  private setValue(obj, keyPath, value) {
    const keys = keyPath.split('->');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    // Set the final value
    current[keys[keys.length - 1]] = value;
  }

  private async setTargetModelData() {
    const sectionsContainer = this.operationData.operationContainer.currentOperation.$$sectionsContainers[0];
    const rootObject = sectionsContainer.rootObject;
    let targetObjects: SuperObjectModel[] = [];

    this.preparing.set(false);
    this.percentage.set(0);

    for (const contextObject of this.operationData.operationContainer.operationContextObjects) {
      let progress = 0;
      let targetObject = await this.objectStorageService.loadObject(contextObject.artifact_id);
      let targetHasUpdates = false;

      for (const field of this.fieldsToKeep()) {
        const currentValue = this.getValue(targetObject, field.key);
        const targetValue = this.getValue(rootObject, field.key);

        if (field.field_type === 'array') {
          let targetArrayValues = [...targetValue];
          const uniqueProps = field?.inline?.model === 'SuperobjectHistoryEventItem'
            ? [] : field?.inline?.unique_props;
          const hasUniqueProps = uniqueProps?.length > 0;

          // Filter array items passed in `field.value` when copying media
          if (hasUniqueProps && field.value && field?.inline?.prop) {
            const targetItemIds = field.value.split(',');
            targetArrayValues = targetArrayValues.filter(item => targetItemIds.includes(item[field.inline.prop]));
          }

          // Remove duplicates based on `unique_props`
          if (hasUniqueProps && currentValue && Array.isArray(currentValue)) {
            targetArrayValues = targetArrayValues.filter(item =>
              !currentValue.some(currentItem => uniqueProps.every(prop => item[prop] === currentItem[prop]))
            );
          }

          if (targetArrayValues.length > 0) {
            this.setValue(targetObject, field.key, [...currentValue || [], ...targetArrayValues.map(item => ({
              ...item,
              no: null,
              order_number: null,
              create: true,
            }))]);
            targetHasUpdates = true;
          }
        } else {
          // For non-arrays, replace existing value
          this.setValue(targetObject, field.key, targetValue);
          targetHasUpdates = true;
        }

        progress = this.percentage() + (100 / this.operationData.operationContainer.operationContextObjects.length);

        this.percentage.set(progress > 100 ? 100 : Math.round(progress));

        if (this.percentage() === 100) {
          this.progressText.set('TRANS__COPY_PROGRESS_DIALOG__DESCRIPTION__PREPARING_STORE')
          this.preparing.set(true);
        }
      }

      if (targetHasUpdates) {
        targetObjects.push(targetObject);
      }
    }

    this.storeTotal.set(targetObjects.length);
    this.storeProgress.set(0);
    this.progressText.set('TRANS__COPY_PROGRESS_DIALOG__DESCRIPTION__COUNT');
    this.percentage.set(0);
    this.preparing.set(false);

    let result = [];

    for (const targetObject of targetObjects) {
      let r = await this.objectStorageService.storeObject(targetObject);
      result.push(r);

      this.storeProgress.update(v => v + 1);
      this.percentage.set(this.storeProgress() * (100 / this.storeTotal()));
    }

    this.percentage.set(100);

    setTimeout(() => {
      this.dialogRef.close(result);
    }, 500);
  }
}
